/**
 * IDP-1291: single search box for the Public Hosted Apps listing.
 *
 * One term is matched against every displayed column (Name, Owner,
 * Description). The filter narrows the entity list that both the card grid and
 * the table read from (via EntityListProvider), so it behaves identically in
 * either layout.
 */
import { Entity, RELATION_OWNED_BY } from '@backstage/catalog-model';
import { EntityFilter } from '@backstage/plugin-catalog-react';

const OWNER_EMAILS_ANNOTATION = 'microapp/owners';

/** Display name shown in the Name column / card title. */
export const getPublicAppDisplayName = (entity: Entity): string =>
  entity.metadata.title || entity.metadata.name;

/**
 * Owners of a public app: the `microapp/owners` emails (the people who manage
 * the app). Falls back to the catalog owner (e.g. "engineering") when the
 * annotation is missing.
 */
export function getPublicAppOwners(entity: Entity): string[] {
  const emails = (entity.metadata.annotations?.[OWNER_EMAILS_ANNOTATION] ?? '')
    .split(',')
    .map(e => e.trim())
    .filter(Boolean);
  if (emails.length > 0) return emails;
  const ownerRef = entity.relations?.find(
    r => r.type === RELATION_OWNED_BY,
  )?.targetRef;
  return ownerRef ? [ownerRef.split('/').pop() ?? ownerRef] : [];
}

/**
 * True when the term (case-insensitive) appears in any displayed column:
 * name (title or technical name), any owner email, or the description.
 * An empty term matches everything.
 */
export function matchesPublicAppSearch(entity: Entity, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  const columnValues = [
    getPublicAppDisplayName(entity),
    entity.metadata.name,
    entity.metadata.description ?? '',
    ...getPublicAppOwners(entity),
  ];
  return columnValues.some(value => value.toLowerCase().includes(needle));
}

/** Catalog EntityFilter wrapper so EntityListProvider applies the search. */
export class PublicAppSearchFilter implements EntityFilter {
  constructor(readonly term: string) {}

  filterEntity(entity: Entity): boolean {
    return matchesPublicAppSearch(entity, this.term);
  }
}
